import React from "react";
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Image as ImageIcon,
  Rocket,
  Settings,
  Settings2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ExpandablePanel } from './ExpandablePanel';
import { UserMenu } from './UserMenu';

type NavSection = 'creatives' | 'launcher' | 'setup' | null;

interface NavItem {
  id: NavSection;
  icon: LucideIcon;
  label: string;
  hasPanel: boolean;
  routePrefix: string;
}

const navItems: NavItem[] = [
  { id: 'creatives', icon: ImageIcon, label: 'Creative Hub', hasPanel: true, routePrefix: '/creatives' },
  { id: 'launcher', icon: Rocket, label: 'Ad Launcher', hasPanel: true, routePrefix: '/launcher' },
  { id: 'setup', icon: Settings2, label: 'Setup', hasPanel: true, routePrefix: '/setup' },
];

const PANEL_STORAGE_KEY = 'nav-panel-state';

// Determine which section should be open based on current route
const getSectionFromRoute = (pathname: string): NavSection => {
  if (pathname.startsWith('/creatives')) return 'creatives';
  if (pathname.startsWith('/launcher')) return 'launcher';
  if (pathname.startsWith('/setup')) return 'setup';
  return null;
};

export function IconRail() {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize panel state from route or localStorage
  const [activeSection, setActiveSection] = useState<NavSection>(() => {
    const routeSection = getSectionFromRoute(location.pathname);
    if (routeSection) return routeSection;
    const stored = localStorage.getItem(PANEL_STORAGE_KEY);
    if (stored && stored !== 'null') return stored as NavSection;
    return null;
  });

  useEffect(() => {
    const routeSection = getSectionFromRoute(location.pathname);
    if (routeSection && routeSection !== activeSection) {
      setActiveSection(routeSection);
      localStorage.setItem(PANEL_STORAGE_KEY, routeSection);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeSection) {
      localStorage.setItem(PANEL_STORAGE_KEY, activeSection);
    }
  }, [activeSection]);

  const isActiveSection = (section: NavSection) => {
    if (!section) return false;
    const item = navItems.find(i => i.id === section);
    return item ? location.pathname.startsWith(item.routePrefix) : false;
  };

  const handleIconClick = (item: NavItem) => {
    if (item.hasPanel) {
      if (activeSection === item.id) {
        setActiveSection(null);
        localStorage.setItem(PANEL_STORAGE_KEY, 'null');
      } else {
        setActiveSection(item.id);
        localStorage.setItem(PANEL_STORAGE_KEY, item.id);
      }
    }
  };

  const handleSettingsClick = () => {
    setActiveSection(null);
    localStorage.setItem(PANEL_STORAGE_KEY, 'null');
    navigate('/settings');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <>
      {/* Icon Rail */}
      <div className="flex flex-col h-full w-[60px] bg-sidebar shrink-0">
        {/* Logo */}
        <div className="flex items-center justify-center h-16 border-b border-sidebar-border">
          <div className="w-8 h-8 rounded-lg bg-sidebar-primary flex items-center justify-center">
            <span className="text-sidebar-primary-foreground font-bold text-sm">TB</span>
          </div>
        </div>

        {/* Nav Icons */}
        <nav className="flex-1 flex flex-col items-center py-4 gap-2">
          {navItems.map((item) => {
            const isActive = isActiveSection(item.id) || activeSection === item.id;
            return (
              <Button
                key={item.id}
                variant="ghost"
                size="icon"
                className={cn(
                  'w-10 h-10 rounded-lg transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-primary'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground'
                )}
                onClick={() => handleIconClick(item)}
                title={item.label}
              >
                <item.icon className="h-5 w-5" />
              </Button>
            );
          })}
        </nav>

        {/* Divider */}
        <div className="mx-3 border-t border-sidebar-border" />

        {/* Bottom Icons */}
        <div className="flex flex-col items-center py-4 gap-2">
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'w-10 h-10 rounded-lg transition-colors',
              location.pathname === '/settings'
                ? 'bg-sidebar-accent text-sidebar-primary'
                : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground'
            )}
            onClick={handleSettingsClick}
            title="Settings"
          >
            <Settings className="h-5 w-5" />
          </Button>

          <UserMenu />
        </div>
      </div>

      {/* Expandable Panel */}
      <ExpandablePanel
        section={activeSection}
        onNavigate={handleNavigate}
      />
    </>
  );
}
